import { useRef, useState } from "preact/hooks";

import { HeatSpeciesList } from "./HeatSpeciesList";
import { WeatherDetailOverlay } from "./WeatherDetailOverlay";
import { alertSeverityTone, alertToneLabel } from "../lib/weatherCopy";
import type { AdvisoryOut, WeatherAlertOut } from "../types";

export interface TodayHeatPillProps {
  advisory: AdvisoryOut | null;
  alerts: WeatherAlertOut[];
}

const TITLE_ID = "today-pill-title";

/**
 * Sprint-015 C5 (weather-hub-ia-layout-2026-07-10.md §6, CD sign-off
 * 2026-07-10 Deviation 3). There is no pre-existing Today Card alert pill
 * in the live source to extend (`TodayView.tsx` renders only
 * `.weather-strip` — a non-interactive, non-severity-toned temp+condition
 * line) — this is entirely NEW build, visually identical to C1's
 * `.heat-pill` (same tone convention, 48px tap target, chevron, tap-to-
 * sheet), placed as its OWN element directly below the untouched
 * `.weather-strip` — purely additive, never a modification of it.
 *
 * Shared-pill precedence (NWS > heat-stress > absent — the CD-confirmed
 * Today Card ordering, "a single shared pill carrying whichever is
 * active/more severe, never two stacked pills"):
 *
 *   IF an NWS alert is active           -> pill carries the alert (tag +
 *                                           event text)
 *   ELSE IF advisory !== null           -> pill carries the heat-stress
 *                                           advisory, one-line pill ONLY —
 *                                           Today Card NEVER renders the
 *                                           C2 hero, regardless of the
 *                                           2+-species escalation math
 *                                           (no room for a hero card by
 *                                           design, the ADR's original
 *                                           "stay light, no map" decision)
 *   ELSE                                -> pill absent entirely (matches
 *                                           C1's all-clear behavior)
 *
 * Never carries the garden chip (§4's explicit Today Card exclusion).
 *
 * Today Card is phone-only (this component's caller, `TodayView.tsx`,
 * only ever mounts it `!isDesktop`), so the shared `WeatherDetailOverlay`
 * container is always the `.heat-sheet` in practice — the same component
 * still makes that `≥1024px` popover/`<1024px` sheet call itself, no
 * special-casing needed here, it simply never observes `≥1024px` given
 * how the caller gates mounting.
 *
 * The NWS branch reuses the SAME shared `WeatherDetailOverlay` sheet
 * container C1 already established for its own detail, rather than
 * inventing a second detail surface — no distinct "existing alert detail"
 * component exists in the live source to extend (`WeatherAlertsBanner` on
 * the Dashboard renders every active alert inline, with no tap target of
 * its own); this is the one new alert-detail surface in the app, and it
 * shares the sheet container rather than being bespoke.
 */
export function TodayHeatPill({ advisory, alerts }: TodayHeatPillProps) {
  const [detailOpen, setDetailOpen] = useState(false);
  const pillRef = useRef<HTMLButtonElement>(null);

  // When multiple alerts are simultaneously active, the pill (one line,
  // no room to enumerate) surfaces the most severe (Extreme/Severe over
  // anything else) rather than an arbitrary/first one — mirrors
  // `alertSeverityTone`'s own "warning" > "watch" ranking.
  const activeAlert =
    alerts.find((a) => alertSeverityTone(a.severity) === "warning") ?? alerts[0] ?? null;

  if (!activeAlert && !advisory) return null;

  if (activeAlert) {
    const tone = alertSeverityTone(activeAlert.severity);
    return (
      <>
        <div class="heat-pill-anchor">
          <button
            ref={pillRef}
            type="button"
            class={`heat-pill${tone === "watch" ? " is-moderate" : ""}`}
            aria-haspopup="dialog"
            onClick={() => setDetailOpen(true)}
          >
            <span class="heat-pill-icon" aria-hidden="true">
              ⚠️
            </span>
            {alertToneLabel(tone)}: {activeAlert.event}
            <span class="heat-pill-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        </div>
        <WeatherDetailOverlay
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          titleId={TITLE_ID}
          title={activeAlert.event}
          triggerRef={pillRef}
        >
          <p class="heat-sheet-sub">{activeAlert.headline}</p>
          <p class="heat-sheet-sub">{activeAlert.description}</p>
        </WeatherDetailOverlay>
      </>
    );
  }

  // `activeAlert` is null past this point, so the top guard above already
  // proves `advisory` is non-null here — re-checked explicitly rather
  // than a non-null assertion.
  if (!advisory) return null;

  const isModerate = advisory.level === "elevated";
  return (
    <>
      <div class="heat-pill-anchor">
        <button
          ref={pillRef}
          type="button"
          class={`heat-pill${isModerate ? " is-moderate" : ""}`}
          aria-haspopup="dialog"
          onClick={() => setDetailOpen(true)}
        >
          <span class="heat-pill-icon" aria-hidden="true">
            🌡️
          </span>
          {advisory.headline}
          <span class="heat-pill-chevron" aria-hidden="true">
            ›
          </span>
        </button>
      </div>
      <WeatherDetailOverlay
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        titleId={TITLE_ID}
        title="Heat-stress advisory"
        triggerRef={pillRef}
      >
        <p class="heat-sheet-sub">{advisory.headline}</p>
        <HeatSpeciesList species={advisory.species} />
      </WeatherDetailOverlay>
    </>
  );
}
